import React, { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Users, Image, TrendingUp, Sparkles, ArrowLeft, Camera, Upload, Settings2, Plus, MessageSquare, Crown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { useCommunityInteraction } from '@/hooks/use-loop-communities';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import PostPromptModal from '@/components/bulletins/PostPromptModal';
import { useBulletinForm } from '@/hooks/use-bulletin-form';
import { useLoops, useLoopActions } from '@/hooks/use-loops';
import { useReblogActions } from '@/hooks/use-reblogs';
import CommunityTabs from '@/components/community/CommunityTabs';
import { TransitionProvider } from '@/components/transitions/TransitionContext';
import TransitionLoopFeed from '@/components/transitions/TransitionLoopFeed';
import ExpandedLoopPost from '@/components/loops/ExpandedLoopPost';
import usePostTransition from '@/hooks/use-post-transition';
import EmojiPicker, { EMOTES } from '@/components/ui/emoji-picker';

interface Community {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  color?: string;
  gradient?: string;
  banner_url?: string;
  avatar_url?: string;
  featured_image_url?: string;
  is_fomo_official: boolean;
  is_admin_curated?: boolean;
  is_auto_generated?: boolean;
  member_count: number;
  post_count: number;
  created_at: string;
  created_by_user_id?: string | null;
  short_tag?: string | null;
  emote?: string | null;
  tag_emote?: string | null;
  tag_code?: string | null;
}

export default function CommunityPage() {
  const { communityName } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { trackInteraction } = useCommunityInteraction();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Post creation modal state
  const [postModalOpen, setPostModalOpen] = useState(false);
  const { handleSubmit: submitBulletin, isUploading } = useBulletinForm(false);
  
  // Community tabs state (default to admin for admin-curated communities)
  const [activeTab, setActiveTab] = useState<string>('admin');
  
  // Track engaged loops
  const [engagedLoops, setEngagedLoops] = React.useState<Set<string>>(new Set());
  
  // Hook for handling post transitions
  const { activeLoop, isPostOpen, openPost, closePost } = usePostTransition();
  
  // Hook for loop actions
  const { likeLoop, unlikeLoop } = useLoopActions();
  const { reblog, quote } = useReblogActions();

  // Fetch community details
  const { data: community, isLoading } = useQuery({
    queryKey: ['community', communityName],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('*')
        .eq('name', communityName)
        .single();

      if (error) throw error;
      return data as Community;
    },
    enabled: !!communityName
  });

  // Fetch community view count
  const { data: viewCount } = useQuery({
    queryKey: ['community-views', community?.id],
    queryFn: async () => {
      if (!community?.id) return 0;
      const { data, error } = await supabase
        .from('user_community_interactions')
        .select('id')
        .eq('community_id', community.id)
        .eq('interaction_type', 'view');
      
      if (error) {
        console.error('Error fetching view count:', error);
        return 0;
      }
      
      return data?.length || 0;
    },
    enabled: !!community?.id
  });

  // Fetch membership state for current user
  const { data: membership, refetch: refetchMembership } = useQuery({
    queryKey: ['community-membership', user?.id, community?.id],
    queryFn: async () => {
      if (!user?.id || !community?.id) return null;
      const { data, error } = await supabase
        .from('user_community_memberships')
        .select('id')
        .eq('user_id', user.id)
        .eq('community_id', community.id)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') { // PGRST116: no rows
        console.error('Membership query error:', error);
        return null;
      }
      return data || null;
    },
    enabled: !!user?.id && !!community?.id
  });

  const isMember = !!membership?.id;
  const joiningRef = React.useRef(false);
  const [isJoinLoading, setIsJoinLoading] = React.useState(false);

  // Handle joining community
  const handleJoinCommunity = async () => {
    if (!user?.id || !community?.id || isJoinLoading) return;
    
    setIsJoinLoading(true);
    try {
      const { error } = await supabase
        .from('user_community_memberships')
        .insert({ user_id: user.id, community_id: community.id });
      
      if (error) throw error;
      
      // Update member count
      await supabase
        .from('communities')
        .update({ member_count: (community.member_count || 0) + 1 })
        .eq('id', community.id);
      
      await Promise.all([
        refetchMembership(),
        queryClient.invalidateQueries({ queryKey: ['community', communityName] }),
      ]);
      
      toast({ 
        title: 'Joined community', 
        description: `Welcome to #${community.name}!` 
      });
    } catch (error: any) {
      toast({ 
        title: 'Failed to join', 
        description: error.message || 'Something went wrong', 
        variant: 'destructive' 
      });
    } finally {
      setIsJoinLoading(false);
    }
  };

  // Handle leaving community
  const handleLeaveCommunity = async () => {
    if (!user?.id || !community?.id || isJoinLoading) return;
    
    // Prevent leaving official FOMO communities
    if (community.is_fomo_official) {
      toast({ 
        title: 'Cannot leave', 
        description: 'You cannot leave official FOMO communities', 
        variant: 'destructive' 
      });
      return;
    }
    
    setIsJoinLoading(true);
    try {
      const { error } = await supabase
        .from('user_community_memberships')
        .delete()
        .eq('user_id', user.id)
        .eq('community_id', community.id);
      
      if (error) throw error;
      
      // Update member count
      await supabase
        .from('communities')
        .update({ member_count: Math.max(0, (community.member_count || 1) - 1) })
        .eq('id', community.id);
      
      await Promise.all([
        refetchMembership(),
        queryClient.invalidateQueries({ queryKey: ['community', communityName] }),
      ]);
      
      toast({ 
        title: 'Left community', 
        description: `You've left #${community.name}` 
      });
    } catch (error: any) {
      toast({ 
        title: 'Failed to leave', 
        description: error.message || 'Something went wrong', 
        variant: 'destructive' 
      });
    } finally {
      setIsJoinLoading(false);
    }
  };

  // Check if community should show tabs (only for official #fomo community)
  const shouldShowTabs = community?.is_fomo_official === true;
  
  // Use loops feed with community filter
  const {
    data: postsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: postsLoading
  } = useLoops('chronological', { communityName: community?.name });

  // Handle tab change
  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab);
    // Invalidate queries to refresh posts for the new tab
    queryClient.invalidateQueries({ queryKey: ['community-posts', community?.id] });
  }, [community?.id, queryClient]);

  const posts = postsData?.pages.flatMap(page => page) || [];
  
  // Handle engagement
  const handleEngage = (loopId: string) => {
    console.log('Engaging with loop:', loopId);
    setEngagedLoops(prev => new Set([...prev, loopId]));
  };

  // Handle post click 
  const handlePostClick = (loop: any) => {
    openPost(loop);
  };

  // Handle like action
  const handleLike = useCallback((loop: any) => {
    const isLiked = loop.is_liked;
    if (isLiked) {
      unlikeLoop(loop.id);
    } else {
      likeLoop(loop.id);
    }
  }, [likeLoop, unlikeLoop]);

  // Handle comment action
  const handleComment = useCallback((loop: any) => {
    // TODO: Implement comment modal or navigation
    console.log('Comment on loop:', loop.id);
  }, []);

  // Handle reblog action
  const handleReblog = useCallback((loop: any) => {
    reblog(loop.id);
  }, [reblog]);

  // Handle quote action
  const handleQuote = useCallback((loop: any) => {
    quote(loop.id, '');
  }, [quote]);

  // Handle post creation
  const handleCreatePost = useCallback(async (
    postCopy: string, 
    mediaTitle: string, 
    mediaDescription: string, 
    image: File | null, 
    isArt: boolean
  ) => {
    if (!community?.id) return;

    // Combine title and description for all posts (both text-only and media)
    let content = postCopy || '';
    if (mediaDescription?.trim()) {
      content = content ? `${content}\n\n${mediaDescription}` : mediaDescription;
    }
    
    // Add community hashtag
    const contentWithHashtag = content.trim() + (content.trim() ? ' ' : '') + `#${community.name}`;
    
    // Use bulletins submission with community hashtag
    const success = await submitBulletin(
      null, // e
      { x: 50, y: 50 }, // focalPoint
      contentWithHashtag, // directBulletinContent 
      image || undefined, // directImage
      postCopy // directLoopsContent (for any cross-posting)
    );

    if (success) {
      // Refresh bulletins feed after creation
      queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      setPostModalOpen(false);
    }
  }, [community, submitBulletin, queryClient]);

  React.useEffect(() => {
    if (community?.id) {
      trackInteraction(community.id, 'view');
    }
  }, [community?.id]);

  // Auto-join: make official communities default by auto-joining on first visit
  React.useEffect(() => {
    const autoJoin = async () => {
      if (!user?.id || !community?.id || !community.is_fomo_official || isMember || joiningRef.current) return;
      try {
        joiningRef.current = true;
        const { error } = await supabase
          .from('user_community_memberships')
          .insert({ user_id: user.id, community_id: community.id });
        if (error) throw error;
        await Promise.all([
          refetchMembership(),
          queryClient.invalidateQueries({ queryKey: ['community', communityName] }),
        ]);
      } catch (e: any) {
        console.warn('Auto-join failed:', e?.message || e);
      } finally {
        joiningRef.current = false;
      }
    };
    autoJoin();
  }, [user?.id, community?.id, community?.is_fomo_official, isMember, refetchMembership, queryClient, communityName]);

  const { isAdmin, isPremium } = useUserRoles();
  const isCreator = !!community && community.created_by_user_id === user?.id;

  // Pixel emote library and helpers (match creation dialog)
  const EMOTES = React.useMemo(() => [
    '🎨','🖼️','✏️','🖌️','🧵','🧶','🪡','🪢','🧩','📐','📏','📎',
    '📸','📷','🎞️','🎬','🎥','📹','🧪','📺','📼','🖥️','🖱️',
    '🎮','🕹️','💻','⌨️','🖲️','🧠','⚙️','🔧','🛠️','🔩','🪛','🧰','🧬','🤖','📡','🛰️','💾','🧪',
    '🎵','🎶','🎧','🎤','🎸','🎻','🥁','🎹','📻','🪩',
    '🌿','🌸','🌻','🌊','🔥','💧','🌙','⭐','☀️','🌈','⛰️','🏝️','🌋','🌪️','🌧️','❄️','🍂','🍃',
    '⚽','🏀','🏈','🎾','🏐','⚾','🥊','🚴','🏃',
    '👗','👟','💄','💍','👜','💅','🕶️',
    '🍕','🍣','🍔','🍜','🍩','🍪','🍫','🍦','🍰','🥐','🥑','🍓','🍎','🍉','🍇','🍒','🍙','🍱',
    '🌸','🪄','✨','💫','🌀','🧸','🎀','🦄','🐉',
  ], []);
  const emojiToCodepoints = (emoji: string) => Array.from(emoji).map(c => c.codePointAt(0)!.toString(16)).join('-');
  const twemojiUrl = (emoji: string) => `https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/72x72/${emojiToCodepoints(emoji)}.png`;

  // Upload helpers
  const fileInputBanner = React.useRef<HTMLInputElement | null>(null);
  const fileInputAvatar = React.useRef<HTMLInputElement | null>(null);
  const fileInputFeatured = React.useRef<HTMLInputElement | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteStep, setDeleteStep] = React.useState<1 | 2>(1);
  const [deleteAcknowledge, setDeleteAcknowledge] = React.useState(false);
  const [confirmHandle, setConfirmHandle] = React.useState('');
  
  // Community tag editing state
  const [editFormData, setEditFormData] = React.useState({
    short_tag: community?.short_tag || '',
    tag_code: community?.tag_code || '',
    tag_emote: community?.tag_emote || '',
    emote: community?.emote || ''
  });

  // Update form data when community changes
  React.useEffect(() => {
    if (community) {
      setEditFormData({
        short_tag: community.short_tag || '',
        tag_code: community.tag_code || '',
        tag_emote: community.tag_emote || '',
        emote: community.emote || ''
      });
    }
  }, [community]);

  const updateCommunityTags = async () => {
    if (!community) return;
    
    // Trim values for validation
    const trimmedShortTag = editFormData.short_tag?.trim() || '';
    const trimmedEmote = editFormData.emote?.trim() || '';
    const trimmedTagEmote = editFormData.tag_emote?.trim() || '';
    
    // Validate short_tag: must be 3-5 alphanumeric characters or empty
    if (trimmedShortTag && !/^[A-Za-z0-9]{3,5}$/.test(trimmedShortTag)) {
      toast({ 
        title: 'Invalid Short Tag', 
        description: 'Short tag must be 3-5 alphanumeric characters (A-Z, 0-9)', 
        variant: 'destructive' 
      });
      return;
    }
    
    // Validate emote: max 4 characters
    if (trimmedEmote && trimmedEmote.length > 4) {
      toast({ 
        title: 'Invalid Emote', 
        description: 'Emote must be 4 characters or less', 
        variant: 'destructive' 
      });
      return;
    }
    
    // Validate tag_emote: max 4 characters
    if (trimmedTagEmote && trimmedTagEmote.length > 4) {
      toast({ 
        title: 'Invalid Tag Emote', 
        description: 'Tag emote must be 4 characters or less', 
        variant: 'destructive' 
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('communities')
        .update({
          short_tag: trimmedShortTag || null,
          tag_code: editFormData.tag_code?.trim() || null,
          tag_emote: trimmedTagEmote || null,
          emote: trimmedEmote || null,
          is_admin_curated: true
        })
        .eq('id', community.id);
        
      if (error) throw error;
      
      toast({ title: 'Community updated', description: 'Tag settings have been updated.' });
      await queryClient.invalidateQueries({ queryKey: ['community', communityName] });
      setEditOpen(false);
    } catch (error: any) {
      console.error('[updateCommunityTags] Error:', error);
      toast({ 
        title: 'Update failed', 
        description: error.message || 'Unknown error', 
        variant: 'destructive' 
      });
    }
  };

  const uploadAndSet = async (file: File, keyPrefix: string, column: 'banner_url' | 'avatar_url' | 'featured_image_url') => {
    if (!community) return;
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const path = `${community.id}/${keyPrefix}-${Date.now()}.${ext}`;

    const { error: upErr } = await supabase.storage.from('community-images').upload(path, file, {
      cacheControl: '3600',
      upsert: true,
      contentType: file.type || 'image/jpeg',
    });
    if (upErr) {
      toast({ title: 'Upload failed', description: upErr.message, variant: 'destructive' });
      return;
    }

    const { data: urlData } = supabase.storage.from('community-images').getPublicUrl(path);
    const publicUrl = urlData.publicUrl;

    const { error: updErr } = await supabase
      .from('communities')
      .update({ [column]: publicUrl, is_admin_curated: true })
      .eq('id', community.id);
    if (updErr) {
      toast({ title: 'Save failed', description: updErr.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Updated community', description: `${column.replace('_', ' ')} updated.` });
    await queryClient.invalidateQueries({ queryKey: ['community', communityName] });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A1A1B' }}>
        <div className="animate-pulse text-white">Loading community...</div>
      </div>
    );
  }

  if (!community) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A1A1B' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">Community not found</h2>
          <Button onClick={() => navigate('/communities')}>
            Browse Communities
          </Button>
        </div>
      </div>
    );
  }

  // Generate default avatar from community icon or first letter
  const defaultAvatar = community.icon || community.display_name[0].toUpperCase();
  const canDeleteCommunity = isAdmin || (community.created_by_user_id === user?.id);

  const performDelete = async () => {
    if (!community) return;
    try {
      console.log('[COMMUNITY] Deleting relations for', community.id);
      // Remove relational links first to avoid FK violations
      try {
        await supabase.from('loop_communities').delete().eq('community_id', community.id);
      } catch (e) {
        console.warn('[COMMUNITY] Failed to delete loop_communities relations (continuing):', e);
      }
      try {
        await supabase.from('user_community_interactions').delete().eq('community_id', community.id);
      } catch (e) {
        console.warn('[COMMUNITY] Failed to delete user_community_interactions (continuing):', e);
      }

      const { error } = await supabase
        .from('communities')
        .delete()
        .eq('id', community.id);
      if (error) throw error;
      toast({ title: 'Community deleted', description: `#${community.name} was removed.` });
      setDeleteOpen(false);
      navigate('/communities');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['all-communities'] }),
        queryClient.invalidateQueries({ queryKey: ['sidebar-communities'] }),
        queryClient.invalidateQueries({ queryKey: ['community', community.name] }),
        queryClient.invalidateQueries({ queryKey: ['community-posts', community.id] }),
      ]);
    } catch (e: any) {
      toast({ title: 'Failed to delete community', description: e.message || 'Unknown error', variant: 'destructive' });
    }
  };
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1A1A1B' }}>
      {/* Main Content */}
      <div className="container mx-auto px-4 py-4">
        {/* Community Info Header - Enhanced Design */}
        <div 
          className="relative rounded-2xl overflow-hidden mb-6 shadow-2xl border border-white/10"
          style={{
            backgroundImage: community.banner_url 
              ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.6)), url(${community.banner_url})`
              : community.gradient 
                ? community.gradient
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Header Controls */}
          <div className="absolute top-4 left-4 flex items-center gap-3 z-10">
            <Button
              onClick={() => navigate('/communities')}
              size="sm"
              variant="outline"
              className="bg-black/20 border-white/20 text-white hover:bg-black/40 backdrop-blur-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>

          {/* Admin Controls - removed from banner, will be moved below */}

          {/* Buttons removed from banner - will be added below */}

          <div className="relative p-4 md:p-8">

            <div className="flex items-start gap-3 md:gap-6">
              {/* Community Avatar */}
              <div className="relative flex-shrink-0">
                {community.avatar_url ? (
                  <img
                    src={community.avatar_url}
                    alt={community.display_name}
                    className="w-16 h-16 md:w-24 md:h-24 rounded-2xl object-cover ring-4 ring-white/20 shadow-xl"
                  />
                ) : (
                  <div className="w-16 h-16 md:w-24 md:h-24 rounded-2xl bg-white/10 ring-4 ring-white/20 shadow-xl flex items-center justify-center text-xl md:text-3xl font-bold text-white backdrop-blur-sm">
                    {typeof defaultAvatar === 'string' && defaultAvatar.match(/\p{Emoji}/u) ? defaultAvatar : defaultAvatar}
                  </div>
                )}
                {community.is_fomo_official && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg">
                    ✓
                  </div>
                )}
              </div>

              {/* Community Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3 flex-wrap">
                  <h1 className="text-2xl md:text-4xl font-bold text-white truncate">{community.display_name}</h1>
                  {community.short_tag && (
                    <div className="px-2 md:px-3 py-1 bg-white/10 rounded-full text-xs md:text-sm font-medium text-white/80 backdrop-blur-sm">
                      #{community.short_tag}
                    </div>
                  )}
                </div>
                
                {community.description && (
                  <p className="text-white/80 mb-3 md:mb-4 max-w-2xl text-sm md:text-base line-clamp-2 md:line-clamp-none">{community.description}</p>
                )}

                {/* Community Stats */}
                <div className="flex items-center gap-3 md:gap-6 text-white/60 flex-wrap">
                  <div className="flex items-center gap-1 md:gap-2">
                    <Users className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="font-medium text-sm md:text-base">{community.member_count.toLocaleString()}</span>
                    <span className="text-xs md:text-sm hidden md:inline">members</span>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2">
                    <MessageSquare className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="font-medium text-sm md:text-base">{community.post_count.toLocaleString()}</span>
                    <span className="text-xs md:text-sm hidden md:inline">posts</span>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2">
                    <TrendingUp className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="font-medium text-sm md:text-base">{viewCount?.toLocaleString() || 0}</span>
                    <span className="text-xs md:text-sm hidden md:inline">views</span>
                </div>
              </div>
            </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
              </div>
            </div>
          </div>
        </div>

        {/* Community Action Buttons - moved below banner */}
        <div className="mb-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 bg-[#1E1E1E] rounded-lg border border-white/10 p-2">
          {/* Join/Leave Button */}
          {user && (
            <div className="flex items-center gap-2">
              <Button
                onClick={isMember ? handleLeaveCommunity : handleJoinCommunity}
                disabled={isJoinLoading}
                size="sm"
                variant={isMember ? "link" : "default"}
                className={cn(
                  "transition-all duration-200 text-xs h-7",
                  isMember 
                    ? "text-white/60 hover:text-red-400 p-0 h-auto font-normal underline-offset-4 hover:underline"
                    : "bg-primary/90 hover:bg-primary text-primary-foreground backdrop-blur-sm min-w-[70px]"
                )}
              >
                {isJoinLoading ? (
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    {isMember ? 'Leaving...' : 'Joining...'}
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    {isMember ? (
                      <>
                        <Users className="w-3 h-3" />
                        <span>Leave</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-3 h-3" />
                        <span>Join</span>
                      </>
                    )}
                  </div>
                )}
              </Button>
            </div>
          )}

          {/* Admin Controls */}
          {(isAdmin || isCreator) && (
            <div className="flex items-center gap-1">
              <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="bg-black/20 border-white/20 text-white hover:bg-black/40 backdrop-blur-sm text-xs h-7 px-2">
                    <Settings2 className="w-3 h-3 mr-1" />
                    <span>Edit</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Community</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6">
                    {/* Community Tag Section */}
                    <div className="border border-white/10 rounded-lg p-4 space-y-4">
                      <h3 className="text-lg font-semibold">Community Tags</h3>
                      
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Short Tag</label>
                          <Input
                            placeholder="e.g. fomo"
                            value={editFormData.short_tag}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, short_tag: e.target.value }))}
                            className="bg-background border-white/20"
                          />
                          <p className="text-xs text-white/60 mt-1">Used for hashtags like #{editFormData.short_tag || 'tag'}</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Tag Code</label>
                          <Input
                            placeholder="e.g. FOMO"
                            value={editFormData.tag_code}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, tag_code: e.target.value }))}
                            className="bg-background border-white/20"
                          />
                          <p className="text-xs text-white/60 mt-1">Display code for members</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Community Emote</label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="🎨"
                              value={editFormData.emote}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, emote: e.target.value }))}
                              className="bg-background border-white/20 flex-1"
                            />
                          </div>
                          <EmojiPicker
                            selectedEmoji={editFormData.emote}
                            onEmojiSelect={(emoji) => setEditFormData(prev => ({ ...prev, emote: emoji }))}
                            className="mt-2"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Tag Emote</label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="✨"
                              value={editFormData.tag_emote}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, tag_emote: e.target.value }))}
                              className="bg-background border-white/20 flex-1"
                            />
                          </div>
                          <EmojiPicker
                            selectedEmoji={editFormData.tag_emote}
                            onEmojiSelect={(emoji) => setEditFormData(prev => ({ ...prev, tag_emote: emoji }))}
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Image Upload Section */}
                    <div className="border border-white/10 rounded-lg p-4 space-y-4">
                      <h3 className="text-lg font-semibold">Images</h3>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Banner Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputBanner}
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadAndSet(e.target.files[0], 'banner', 'banner_url')}
                        />
                        <Button onClick={() => fileInputBanner.current?.click()} variant="outline">
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Banner
                        </Button>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Avatar</label>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputAvatar}
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadAndSet(e.target.files[0], 'avatar', 'avatar_url')}
                        />
                        <Button onClick={() => fileInputAvatar.current?.click()} variant="outline">
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Avatar
                        </Button>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Featured Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputFeatured}
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadAndSet(e.target.files[0], 'featured', 'featured_image_url')}
                        />
                        <Button onClick={() => fileInputFeatured.current?.click()} variant="outline">
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Featured
                        </Button>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2 justify-end pt-4 border-t border-white/10">
                      <Button variant="outline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditOpen(false); }}>
                        Cancel
                      </Button>
                      <Button onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateCommunityTags(); }}>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {canDeleteCommunity && (
                <Button
                  onClick={() => setDeleteOpen(true)}
                  size="sm"
                  variant="link"
                  className="text-red-400 hover:text-red-300 text-xs px-2 h-7"
                >
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Community Tabs */}
        <CommunityTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          communityId={community?.id || ''}
          showTabs={shouldShowTabs}
        />

        {/* Post Creation Section */}
        {user && (
          <div className="bg-[#1E1E1E] rounded-xl border border-white/10 mb-4">
            <div className="p-4">
              <div 
                className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => setPostModalOpen(true)}
              >
                <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-white/60" />
                </div>
                <div className="flex-1 text-white/60">
                  Share something with the {community?.display_name} community...
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Posts Section */}
        <div className="bg-[#1E1E1E] rounded-xl border border-white/10">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                {shouldShowTabs && activeTab === 'admin' ? 'Official Posts' :
                 shouldShowTabs && activeTab === 'community' ? 'Community Posts' :
                 'Posts'}
              </h2>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <Sparkles className="w-4 h-4" />
                <span>Latest activity</span>
              </div>
            </div>

            <TransitionProvider>
              {postsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-white/60">Loading posts...</div>
                </div>
              ) : (
                <AnimatePresence>
                  {/* If a post is expanded, show expanded view */}
                  {isPostOpen && activeLoop ? (
                    <div className="mb-4 w-full">
                      <ExpandedLoopPost 
                        loop={activeLoop!} 
                        onClose={closePost} 
                      />
                    </div>
                  ) : (
                    // Show the regular feed
                  <div>
                    {posts.length > 0 ? (
                      <div className="space-y-4">
                        {posts.map((loop) => (
                          <motion.div
                            key={loop.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <TransitionLoopFeed
                              loop={loop}
                              onEngage={handleEngage}
                              isEngaged={engagedLoops.has(loop.id)}
                              onPostClick={handlePostClick}
                              onLike={handleLike}
                              onComment={handleComment}
                              onReblog={handleReblog}
                              onQuote={handleQuote}
                            />
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-white/60">
                        No posts yet in this community
                      </div>
                    )}
                    
                    {hasNextPage && (
                      <div className="p-6 text-center">
                        <Button
                          variant="outline"
                          onClick={() => fetchNextPage()}
                          disabled={isFetchingNextPage}
                        >
                          {isFetchingNextPage ? 'Loading...' : 'Load More Posts'}
                        </Button>
                      </div>
                    )}
                  </div>
                  )}
                </AnimatePresence>
              )}

            </TransitionProvider>
          </div>
        </div>

        {/* Post Creation Modal */}
        <PostPromptModal
          open={postModalOpen}
          onClose={() => setPostModalOpen(false)}
          onSubmit={handleCreatePost}
          title={`Share your content with the ${community?.display_name} community`}
          description=""
          uploadProgress={{
            progress: 0,
            state: 'idle'
          }}
          isPremium={isPremium}
          isAdmin={isAdmin}
        />

        {/* Delete confirmation dialog */}
        {(isAdmin || canDeleteCommunity) && (
          <Dialog open={deleteOpen} onOpenChange={(v) => { setDeleteOpen(v); if (!v) { setDeleteStep(1); setDeleteAcknowledge(false); setConfirmHandle(''); } }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete community</DialogTitle>
              </DialogHeader>
              {deleteStep === 1 ? (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Are you sure you want to permanently delete <strong>#{community.name}</strong>?
                  </p>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone. All posts and data will be lost.
                  </p>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={deleteAcknowledge}
                      onChange={(e) => setDeleteAcknowledge(e.target.checked)}
                    />
                    <span className="text-sm">I understand this action is permanent</span>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={!deleteAcknowledge}
                      onClick={() => setDeleteStep(2)}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    To confirm, type the community handle <strong>{community.name}</strong> below:
                  </p>
                  <input
                    type="text"
                    value={confirmHandle}
                    onChange={(e) => setConfirmHandle(e.target.value)}
                    placeholder={community.name}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setDeleteStep(1)}>
                      Back
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={confirmHandle !== community.name}
                      onClick={performDelete}
                    >
                       Delete Community
                     </Button>
                   </div>
                 </div>
               )}
             </DialogContent>
           </Dialog>
         )}
       </div>
     </div>
   );
 }
